import { api } from '../api';
import { logError } from '../../utils/logger';
import { DataValidators } from '../../utils/validators';

// CORREÇÃO: Interface mais flexível para escolaridade
export interface Paciente {
  id: string;
  nome: string;
  dataNascimento?: string;
  servico: 'psicoterapia' | 'avaliacao' | 'orientacao' | 'terapia_casal' | 'triagem';
  telefone: string;
  email?: string;
  cpf?: string;
  rg?: string;
  estado_civil?: 'solteiro' | 'casado' | 'divorciado' | 'viuvo' | 'uniao_estavel';
  profissao?: string;
  escolaridade?: string; // CORREÇÃO: Mudança para string genérico para flexibilidade
  genero?: 'masculino' | 'feminino' | 'outro' | 'nao_informar';
  endereco?: string;
  cep?: string;
  cidade?: string;
  estado?: string;
  nome_emergencia?: string;
  telefone_emergencia?: string;
  parentesco_emergencia?: string;
  observacoes?: string;
  linkConsultaOnline?: string;
  historicoMedico?: string;
  problemasClinicos?: Record<string, boolean>;
  dataPrimeiraConsulta?: string;
  ativo?: boolean;
  createdAt?: string;
  updatedAt?: string;
  usuario_responsavel?: number;  // ✅ MULTI-PSICÓLOGOS: ID do psicólogo responsável
}

// CORREÇÃO: Interface específica para criação com validação de escolaridade
export interface CriarPacienteInput {
  nome: string;
  dataNascimento?: string;
  servico: 'psicoterapia' | 'avaliacao' | 'orientacao' | 'terapia_casal' | 'triagem';
  telefone: string;
  email?: string;
  cpf?: string;
  rg?: string;
  estado_civil?: 'solteiro' | 'casado' | 'divorciado' | 'viuvo' | 'uniao_estavel';
  profissao?: string;
  escolaridade?: 'fundamental_incompleto' | 'fundamental_completo' | 'medio_incompleto' | 
    'medio_completo' | 'superior_incompleto' | 'superior_completo' | 
    'pos_graduacao' | 'mestrado' | 'doutorado'; // CORREÇÃO: Union type específico apenas para entrada
  genero?: 'masculino' | 'feminino' | 'outro' | 'nao_informar';
  endereco?: string;
  cep?: string;
  cidade?: string;
  estado?: string;
  nome_emergencia?: string;
  telefone_emergencia?: string;
  parentesco_emergencia?: string;
  observacoes?: string;
  linkConsultaOnline?: string;
  historicoMedico?: string;
  problemasClinicos?: Record<string, boolean>;
  dataPrimeiraConsulta?: string;
  usuario_responsavel?: number;  // ✅ MULTI-PSICÓLOGOS: Psicólogo responsável pelo paciente
}

/**
 * Service for managing patient data
 */
export const pacientesService = {
  /**
   * Get all patients
   * @param incluirInativos Whether to include inactive patients
   * @param psicologoId Optional filter by psychologist ID (multi-psicólogos)
   */
  async listarTodos(incluirInativos: boolean = false, psicologoId?: number): Promise<Paciente[]> {
    try {
      // ✅ MULTI-PSICÓLOGOS: Construir URL com parâmetros
      const params: any = { incluirInativos };
      if (psicologoId) {
        params.psicologoId = psicologoId;
      }
      
      const response = await api.get<Paciente[]>('/pacientes', { params });
      
      // CORREÇÃO: Corrigir uso de função que retorna void
      const pacientesData = Array.isArray(response.data) ? response.data : [];
      
      // CORREÇÃO: Verificar se a função existe E está implementada antes de chamar
      if (typeof DataValidators.validatePacientes === 'function' && 
          DataValidators.validatePacientes !== Object.prototype[DataValidators.validatePacientes as unknown as keyof Object]) {
        try {
          DataValidators.validatePacientes(pacientesData);
        } catch (validationError) {
          console.warn('Erro na validação de pacientes, continuando sem validação:', validationError);
          // Continuar a execução mesmo com erro na validação
        }
      }
      
      // CORREÇÃO: Garantir que todos os campos obrigatórios estão presentes e corretamente tipados
  return pacientesData.map((paciente: any) => {
        // CORREÇÃO: Normalizar o campo gênero para um valor aceito pela interface
        let generoNormalizado: 'masculino' | 'feminino' | 'outro' | 'nao_informar' | undefined = undefined;
        if (paciente.genero) {
          const generoLower = typeof paciente.genero === 'string' ? paciente.genero.toLowerCase() : '';
          if (['masculino', 'feminino', 'outro', 'nao_informar'].includes(generoLower)) {
            generoNormalizado = generoLower as 'masculino' | 'feminino' | 'outro' | 'nao_informar';
          }
        }

        // CORREÇÃO: Normalizar estado_civil para tipos permitidos
        let estadoCivilNormalizado: 'solteiro' | 'casado' | 'divorciado' | 'viuvo' | 'uniao_estavel' | undefined = undefined;
        if (paciente.estado_civil) {
          const estadoLower = typeof paciente.estado_civil === 'string' ? paciente.estado_civil.toLowerCase() : '';
          if (['solteiro', 'casado', 'divorciado', 'viuvo', 'uniao_estavel'].includes(estadoLower)) {
            estadoCivilNormalizado = estadoLower as 'solteiro' | 'casado' | 'divorciado' | 'viuvo' | 'uniao_estavel';
          }
        }

        return {
          id: DataValidators.safeString(paciente.id),
          nome: DataValidators.safeString(paciente.nome),
          dataNascimento: DataValidators.safeDate(paciente.dataNascimento),
          servico: paciente.servico || 'psicoterapia',
          telefone: DataValidators.safeString(paciente.telefone),
          email: DataValidators.safeString(paciente.email),
          cpf: DataValidators.safeString(paciente.cpf),
          rg: DataValidators.safeString(paciente.rg),
          estado_civil: estadoCivilNormalizado,
          profissao: DataValidators.safeString(paciente.profissao),
          escolaridade: paciente.escolaridade,
          genero: generoNormalizado,
          endereco: DataValidators.safeString(paciente.endereco),
          cep: DataValidators.safeString(paciente.cep),
          cidade: DataValidators.safeString(paciente.cidade),
          estado: DataValidators.safeString(paciente.estado),
          nome_emergencia: DataValidators.safeString(paciente.nome_emergencia),
          telefone_emergencia: DataValidators.safeString(paciente.telefone_emergencia),
          parentesco_emergencia: DataValidators.safeString(paciente.parentesco_emergencia),
          observacoes: DataValidators.safeString(paciente.observacoes),
          linkConsultaOnline: DataValidators.safeString(paciente.linkConsultaOnline),
          historicoMedico: DataValidators.safeString(paciente.historicoMedico),
          problemasClinicos: paciente.problemasClinicos || {},
          dataPrimeiraConsulta: DataValidators.safeDate(paciente.dataPrimeiraConsulta),
          ativo: paciente.ativo !== false, // Padrão para true se não especificado
          createdAt: DataValidators.safeString(paciente.createdAt),
          // ✅ MULTI-PSICÓLOGOS: normalizar propriedade para filtros no frontend
          usuario_responsavel: typeof paciente.usuario_responsavel === 'number' 
            ? paciente.usuario_responsavel 
            : (typeof paciente.usuarioResponsavel === 'number' ? paciente.usuarioResponsavel : undefined)
        } as Paciente;  // Forçar o tipo após normalização
      });
    } catch (error) {
      console.error('Erro ao buscar pacientes:', error);
      return []; // Retornar array vazio em caso de erro
    }
  },
  
  /**
   * Get patient by ID
   */
  async obterPorId(id: string, incluirInativos: boolean = false): Promise<Paciente | null> {
    try {
      // Adicionar parâmetro para incluir inativos se solicitado
      const url = incluirInativos 
        ? `/pacientes/${id}?incluirInativos=true` 
        : `/pacientes/${id}`;
        
      const response = await api.get<any>(url);
      const data = response.data || {};
      // ✅ Normalizar propriedade do responsável para snake_case, usada no formulário
      if (data && typeof data === 'object') {
        if (data.usuario_responsavel === undefined && typeof data.usuarioResponsavel === 'number') {
          data.usuario_responsavel = data.usuarioResponsavel;
        }
      }
      return data as Paciente;
    } catch (error) {
      logError('PacientesService', `Erro ao obter paciente ${id}`, error);
      return null;
    }
  },

  /**
   * Create new patient
   */
  async criar(paciente: CriarPacienteInput): Promise<Paciente> {
    try {
      // Log what we're sending for debugging
      console.log('Creating patient with data:', JSON.stringify(paciente));
      const response = await api.post<Paciente>('/pacientes', paciente);
      return response.data;
    } catch (error: any) {
      // Identificar o erro personalizado de paciente inativo
      if (error.response?.status === 409 && error.response?.data?.tipo === 'PACIENTE_INATIVO') {
        const customError = new Error(error.response.data.mensagem);
        customError.name = 'PacienteInativoError';
        // Adicionar informações sobre o paciente inativo ao erro
        (customError as any).pacienteInativo = error.response.data.pacienteInativo;
        
        logError('PacientesService', 'Erro: Paciente inativo', error);
        throw customError;
      }
      
      // Capturar erro de CPF duplicado
      if (error.response?.status === 409 && error.response?.data?.erro === 'CPF_DUPLICADO') {
        const customError = new Error(error.response.data.mensagem);
        customError.name = 'CPFDuplicadoError';
        
        logError('PacientesService', 'Erro: CPF duplicado', error);
        throw customError;
      }
      
      logError('PacientesService', 'Erro ao criar paciente', error);
      throw error;
    }
  },

  /**
   * Update patient
   */
  async atualizar(id: string, paciente: Partial<CriarPacienteInput>): Promise<Paciente> {
    try {
      // Log what we're updating for debugging
      console.log('Updating patient with data:', JSON.stringify(paciente));
      const response = await api.put<Paciente>(`/pacientes/${id}`, paciente);
      return response.data;
    } catch (error) {
      logError('PacientesService', `Erro ao atualizar paciente ${id}`, error);
      throw error;
    }
  },

  /**
   * Delete patient (logical deletion)
   */
  async excluir(id: string): Promise<boolean> {
    try {
      await api.delete(`/pacientes/${id}`);
      return true;
    } catch (error) {
      logError('PacientesService', `Erro ao excluir paciente ${id}`, error);
      throw error;
    }
  },
  
  /**
   * Reactivate inactive patient
   */
  async reativar(id: string, pacienteData: Partial<Paciente>): Promise<Paciente> {
    try {
      const response = await api.post<{paciente: Paciente}>(`/pacientes/${id}/reativar`, pacienteData);
      return response.data.paciente;
    } catch (error) {
      logError('PacientesService', `Erro ao reativar paciente ${id}`, error);
      throw error;
    }
  },

  /**
   * Delete patient permanently (physical deletion)
   */
  async excluirDefinitivamente(id: string): Promise<boolean> {
    try {
      await api.delete(`/pacientes/${id}/permanente`);
      return true;
    } catch (error) {
      logError('PacientesService', `Erro ao excluir definitivamente paciente ${id}`, error);
      throw error;
    }
  },
  
  // ============================================
  // ✅ MULTI-PSICÓLOGOS: Novos métodos
  // ============================================
  
  /**
   * Buscar pacientes do psicólogo logado
   */
  async meusPacientes(): Promise<Paciente[]> {
    try {
      const response = await api.get<{ sucesso: boolean; dados: Paciente[] }>('/pacientes/meus-pacientes');
      return response.data.dados || [];
    } catch (error) {
      logError('PacientesService', 'Erro ao buscar meus pacientes', error);
      throw error;
    }
  },
  
  /**
   * Transferir paciente para outro psicólogo
   */
  async transferirPaciente(
    pacienteId: string, 
    novoResponsavelId: number, 
    motivo: string
  ): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const response = await api.post<{ sucesso: boolean; mensagem: string }>(
        `/pacientes/${pacienteId}/transferir`,
        { novoResponsavelId, motivo }
      );
      return response.data;
    } catch (error) {
      logError('PacientesService', `Erro ao transferir paciente ${pacienteId}`, error);
      throw error;
    }
  }
};
